import React, { useState, useEffect } from 'react';
import { Alert } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { Users, TrendingUp, DollarSign, Activity as ActivityIcon } from 'lucide-react';
import api from '../services/api';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 }
};

const containerVariants = {
  animate: { transition: { staggerChildren: 0.08 } }
};

const itemVariants = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] } }
};

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/dashboard/stats');
      setStats(response.data);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load dashboard stats';
      setError(errorMessage);
      console.error('Dashboard error:', err.response?.data || err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit">
        <Alert variant="danger">{error}</Alert>
      </motion.div>
    );
  }

  const revenueData = stats?.revenueByMonth?.map(item => ({
    month: new Date(item.month).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
    revenue: (item.total_cents / 100).toFixed(2)
  })) || [];

  const statCards = [
    { label: 'Total Users', value: stats?.totalUsers || 0, icon: Users, color: '#7B2FF7', bg: 'rgba(123, 47, 247, 0.08)' },
    { label: 'Pro Subscriptions', value: stats?.activeSubscriptions || 0, icon: TrendingUp, color: '#10b981', bg: 'rgba(16, 185, 129, 0.08)' },
    { label: 'Total Revenue', value: `$${((stats?.totalRevenueCents || 0) / 100).toFixed(2)}`, icon: DollarSign, color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.08)' },
    { label: 'Active (7d)', value: stats?.activeUsers || 0, icon: ActivityIcon, color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.08)' },
  ];

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Platform overview and key metrics</p>
        </div>
      </div>

      {/* Stat Cards — Bento Grid */}
      <motion.div
        className="bento-grid"
        variants={containerVariants}
        initial="initial"
        animate="animate"
      >
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.label}
              className="glass-card stat-card"
              variants={itemVariants}
              whileHover={{ y: -4, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.08)' }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <div className="stat-icon" style={{ background: card.bg, color: card.color }}>
                <Icon size={22} strokeWidth={2} />
              </div>
              <div className="stat-value">{card.value}</div>
              <div className="stat-label">{card.label}</div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Second Row — Bento Grid */}
      <motion.div
        className="bento-grid"
        style={{ gridTemplateColumns: '1fr 1fr', marginTop: '16px' }}
        variants={containerVariants}
        initial="initial"
        animate="animate"
      >
        {/* Users by Role */}
        <motion.div className="glass-card" variants={itemVariants}>
          <div className="card-header-row">
            <h3 className="card-title">Users by Role</h3>
          </div>
          <div className="role-list">
            {stats?.usersByRole?.map((item) => (
              <div key={item.role} className="role-item">
                <span className="role-name">{item.role}</span>
                <span className="role-count">{item.count}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Recent Activity + Registrations */}
        <motion.div className="glass-card" variants={itemVariants}>
          <div className="card-header-row">
            <h3 className="card-title">Quick Stats</h3>
          </div>
          <div className="stat-highlight">
            <div className="stat-highlight-value">{stats?.recentRegistrations || 0}</div>
            <div className="stat-highlight-label">New Registrations<br />Last 30 Days</div>
          </div>
        </motion.div>
      </motion.div>

      {/* Revenue Chart */}
      {revenueData.length > 0 && (
        <motion.div
          className="glass-card"
          style={{ marginTop: '16px' }}
          variants={itemVariants}
          initial="initial"
          animate="animate"
        >
          <div className="card-header-row">
            <h3 className="card-title">Revenue Trend</h3>
            <span className="card-subtitle">Last 12 months</span>
          </div>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#7B2FF7" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#7B2FF7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12, fill: '#94a3b8' }}
                  axisLine={{ stroke: 'rgba(0,0,0,0.06)' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#94a3b8' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `$${v}`}
                />
                <Tooltip
                  contentStyle={{
                    background: 'rgba(255,255,255,0.95)',
                    border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
                    fontSize: '13px',
                    padding: '12px 16px'
                  }}
                  formatter={(value) => [`$${value}`, 'Revenue']}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#7B2FF7"
                  strokeWidth={2.5}
                  fill="url(#revenueGradient)"
                  dot={{ fill: '#7B2FF7', r: 4, strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 6, fill: '#7B2FF7', stroke: '#fff', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

export default Dashboard;
